<template>
  <div class="mail-editor">
    <div class="toolbar">
      <template v-for="(group, gIndex) in toolbar" :key="gIndex">
        <a-space>
          <template v-for="item in group.items" :key="item.command || item.action">
            <a-select
                v-if="item.type === 'variables' && variables"
                size="small"
                style="width:170px"
                placeholder="Переменные"
                @change="insertVariable"
            >
              <a-select-option
                  v-for="v in variables"
                  :key="v.value"
                  :value="v.value"
              >
                {{ v.label }}
              </a-select-option>
            </a-select>

            <a-dropdown
                v-else-if="item.type === 'tableMenu'"
                trigger="click"
            >
              <a-button size="small" class="secondary-button">
                <template #icon>
                  <TableOutlined />
                </template>
              </a-button>

              <template #overlay>
                <a-menu>
                  <a-menu-item
                      v-for="tableAction in item.items"
                      :key="tableAction.action"
                      @click="handleAction(tableAction)"
                  >
                    {{ tableAction.label }}
                  </a-menu-item>
                </a-menu>
              </template>
            </a-dropdown>

            <a-popover
                v-else-if="item.action === 'link'"
                trigger="click"
            >
              <template #content>
                <a-input
                    v-model:value="linkUrl"
                    placeholder="https://..."
                    style="width:200px"
                />
                <div style="margin-top:8px">
                  <a-button
                      class="primary-button"
                      size="small"
                      @click="applyLink"
                  >
                    Применить
                  </a-button>
                </div>
              </template>

              <a-button
                  size="small"
                  class="secondary-button"
              >
                <LinkOutlined />
              </a-button>
            </a-popover>

            <a-popover
                v-else-if="item.command === 'toggleHighlight'"
                trigger="click"
            >
              <template #content>
                <div class="highlight-picker">
                  <div
                      v-for="color in highlightColors"
                      :key="color"
                      class="highlight-color"
                      :style="{ background: color }"
                      @click="applyHighlight(color)"
                  />
                </div>

                <a-button
                    size="small"
                    class="secondary-button"
                    style="margin-top:8px"
                    @click="editor.chain().focus().unsetHighlight().run()"
                >
                  Очистить
                </a-button>
              </template>

              <a-button size="small" class="secondary-button">
                <HighlightOutlined style="transform: rotate(90deg)" />
              </a-button>
            </a-popover>

            <a-select
                v-else-if="item.type === 'fontFamily'"
                size="small"
                style="width: 180px"
                :value="selectedFontFamily"
                @change="applyFontFamily"
            >
              <a-select-option
                  v-for="font in fontFamilyOptions"
                  :key="font.value"
                  :value="font.value"
              >
                {{ font.label }}
              </a-select-option>
            </a-select>

            <a-select
                v-else-if="item.type === 'fontSize'"
                size="small"
                style="width: 70px"
                :value="selectedFontSize"
                @change="applyFontSize"
            >
              <a-select-option
                  v-for="size in fontSizeOptions"
                  :key="size.value"
                  :value="size.value"
              >
                {{ size.label }}
              </a-select-option>
            </a-select>

            <a-button
                v-else
                size="small"
                class="secondary-button"
                :class="{ active: isActive(item) }"
                @click="handleAction(item)"
            >
              <component v-if="item.icon" :is="item.icon" />
              <span v-else>{{ item.label }}</span>
            </a-button>
          </template>
        </a-space>

        <a-divider
            v-if="gIndex !== toolbar.length - 1"
            type="vertical"
        />
      </template>

      <input
          ref="imageInput"
          id="image-input"
          type="file"
          hidden
          accept="image/*"
          @change="onImageChange"
      />
    </div>

    <EditorContent :editor="editor" class="editor-content" />
  </div>

  <div class="reset-template">
    <a-popconfirm
        placement="topRight"
        :disabled="type === 'default'"
        :icon="null"
        ok-text="Подтвердить"
        cancel-text="Отменить"
        overlay-class-name="reset-template-popconfirm"
        @confirm="confirmReset"
    >
      <template #title>
        <div>
          <div class="pc-head">
            <ExclamationCircleFilled class="pc-warn-ic" />
            <div class="pc-text">
              <div class="pc-title">Подтвердите действие</div>
            </div>
          </div>

          <div class="pc-desc">
            Ранее введённый текст будет заменён без возможности восстановления.
          </div>
        </div>
      </template>

      <a-button class="secondary-button" :disabled="type === 'default'">
        <template #icon>
          <ReloadOutlined />
        </template>
        Вернуть по умолчанию
      </a-button>
    </a-popconfirm>
  </div>
</template>

<script>
import { onBeforeUnmount, ref, watch } from "vue";
import { Editor, EditorContent, Extension } from "@tiptap/vue-3";
import { Mark, mergeAttributes } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";

import {
  AlignCenterOutlined,
  AlignLeftOutlined,
  AlignRightOutlined,
  BoldOutlined,
  ExclamationCircleFilled,
  HighlightOutlined,
  ItalicOutlined,
  LinkOutlined,
  MenuOutlined,
  OrderedListOutlined,
  PictureOutlined,
  ReloadOutlined,
  StrikethroughOutlined,
  TableOutlined,
  UnderlineOutlined
} from "@ant-design/icons-vue";

import Underline from "@tiptap/extension-underline";
import TextStyle from "@tiptap/extension-text-style";
import TextAlign from "@tiptap/extension-text-align";
import Highlight from "@tiptap/extension-highlight";
import Link from "@tiptap/extension-link";
import Superscript from "@tiptap/extension-superscript";
import Subscript from "@tiptap/extension-subscript";
import Image from "@tiptap/extension-image";
import Color from "@tiptap/extension-color";
import Table from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableHeader from "@tiptap/extension-table-header";
import TableCell from "@tiptap/extension-table-cell";

export default {
  name: "mail-editor",

  components: {
    EditorContent,
    ReloadOutlined,
    LinkOutlined,
    HighlightOutlined,
    TableOutlined,
    ExclamationCircleFilled
  },

  emits: ["reset", "update:content"],

  props: {
    content: String,
    type: String,
    variables: Array
  },

  setup(props, { emit }) {
    const editor = ref(null);
    const linkUrl = ref("");
    const imageInput = ref(null);

    const toolbar = [
      {
        group: "block",
        items: [
          { icon: MenuOutlined, command: "toggleBulletList", active: "bulletList" },
          { icon: OrderedListOutlined, command: "toggleOrderedList", active: "orderedList" }
        ]
      },
      {
        group: "inline",
        items: [
          { icon: BoldOutlined, command: "toggleBold", active: "bold" },
          { icon: ItalicOutlined, command: "toggleItalic", active: "italic" },
          { icon: StrikethroughOutlined, command: "toggleStrike", active: "strike" },
          { icon: UnderlineOutlined, command: "toggleUnderline", active: "underline" },
          { icon: HighlightOutlined, command: "toggleHighlight", active: "highlight" },
          { action: "link" },
          { type: "variables" }
        ]
      },
      {
        group: "script",
        items: [
          { label: "x²", command: "toggleSuperscript", active: "superscript" },
          { label: "x₂", command: "toggleSubscript", active: "subscript" }
        ]
      },
      {
        group: "table",
        items: [
          {
            type: "tableMenu",
            icon: TableOutlined,
            items: [
              { label: "Таблица", action: "insert-table" },
              { label: "+ Ряд", action: "add-row-after" },
              { label: "+ Колонка", action: "add-column-after" },
              { label: "Удалить ряд", action: "delete-row" },
              { label: "Удалить колонку", action: "delete-column" },
              { label: "Удалить таблицу", action: "delete-table" }
            ]
          }
        ]
      },
      {
        group: "align",
        items: [
          { icon: AlignLeftOutlined, action: "align-left" },
          { icon: AlignCenterOutlined, action: "align-center" },
          { icon: AlignRightOutlined, action: "align-right" }
        ]
      },
      {
        group: "media",
        items: [
          { icon: PictureOutlined, action: "image" }
        ]
      },
      {
        group: "text",
        items: [
          { type: "fontFamily" },
          { type: "fontSize" }
        ]
      }
    ];

    const MailVariable = Mark.create({
      name: "mailVariable",
      inclusive: false,

      addAttributes() {
        return {
          class: {
            default: "mail-variable",
            parseHTML: (element) => element.getAttribute("class") || "mail-variable",
            renderHTML: (attributes) => ({
              class: attributes.class
            })
          }
        };
      },

      parseHTML() {
        return [
          {
            tag: "span.mail-variable"
          },
          {
            tag: "span.mail-variable-link",
            getAttrs: () => ({
              class: "mail-variable mail-variable-link"
            })
          }
        ];
      },

      renderHTML({ HTMLAttributes }) {
        return ["span", mergeAttributes(HTMLAttributes), 0];
      }
    });

    const FontSize = Extension.create({
      name: "fontSize",

      addOptions() {
        return {
          types: ["textStyle"]
        };
      },

      addGlobalAttributes() {
        return [
          {
            types: this.options.types,
            attributes: {
              fontSize: {
                default: null,
                parseHTML: (element) => element.style.fontSize || null,
                renderHTML: (attributes) => {
                  if (!attributes.fontSize) {
                    return {};
                  }

                  return {
                    style: `font-size: ${attributes.fontSize}`
                  };
                }
              }
            }
          }
        ];
      },

      addCommands() {
        return {
          setFontSize:
              (fontSize) =>
                  ({ chain }) => {
                    return chain().setMark("textStyle", { fontSize }).run();
                  },

          unsetFontSize:
              () =>
                  ({ chain }) => {
                    return chain()
                        .setMark("textStyle", { fontSize: null })
                        .removeEmptyTextStyle()
                        .run();
                  }
        };
      }
    });

    const FontFamilyExtension = Extension.create({
      name: "fontFamily",

      addOptions() {
        return {
          types: ["textStyle"]
        };
      },

      addGlobalAttributes() {
        return [
          {
            types: this.options.types,
            attributes: {
              fontFamily: {
                default: null,
                parseHTML: (element) => element.style.fontFamily || null,
                renderHTML: (attributes) => {
                  if (!attributes.fontFamily) {
                    return {};
                  }

                  return {
                    style: `font-family: ${attributes.fontFamily}`
                  };
                }
              }
            }
          }
        ];
      },

      addCommands() {
        return {
          setFontFamily:
              (fontFamily) =>
                  ({ chain }) => {
                    return chain().setMark("textStyle", { fontFamily }).run();
                  },

          unsetFontFamily:
              () =>
                  ({ chain }) => {
                    return chain()
                        .setMark("textStyle", { fontFamily: null })
                        .removeEmptyTextStyle()
                        .run();
                  }
        };
      }
    });

    editor.value = new Editor({
      extensions: [
        StarterKit,
        Underline,
        Highlight.configure({
          multicolor: true
        }),
        Superscript,
        Subscript,
        TextStyle,
        Color,
        Image.configure({ allowBase64: true }),
        TextAlign.configure({ types: ["heading", "paragraph"] }),
        Link.configure({
          openOnClick: false
        }),
        Table.configure({
          resizable: true
        }),
        TableRow,
        TableHeader,
        TableCell,
        MailVariable,
        FontSize,
        FontFamilyExtension
      ],
      content: props.content,
      editorProps: {
        attributes: {
          class: "mail-editor-area"
        }
      },
      onUpdate: ({ editor }) => {
        emit("update:content", editor.getHTML());
      }
    });

    const handleAction = (item) => {
      if (!editor.value) return;

      const chain = editor.value.chain().focus();

      if (item.command) {
        chain[item.command]().run();
        return;
      }

      switch (item.action) {
        case "align-left":
          chain.setTextAlign("left").run();
          break;

        case "align-center":
          chain.setTextAlign("center").run();
          break;

        case "align-right":
          chain.setTextAlign("right").run();
          break;

        case "image":
          imageInput.value?.click();
          break;

        case "insert-table":
          chain.insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
          break;

        case "add-row-after":
          chain.addRowAfter().run();
          break;

        case "add-column-after":
          chain.addColumnAfter().run();
          break;

        case "delete-row":
          chain.deleteRow().run();
          break;

        case "delete-column":
          chain.deleteColumn().run();
          break;

        case "delete-table":
          chain.deleteTable().run();
          break;
      }
    };

    const isActive = (item) => {
      if (!item.active) return false;
      return editor.value?.isActive(item.active);
    };

    const selectedFontSize = ref("14px");
    const selectedFontFamily = ref("Arial, Helvetica, sans-serif");

    const fontSizeOptions = [
      { label: "12", value: "12px" },
      { label: "14", value: "14px" },
      { label: "16", value: "16px" },
      { label: "18", value: "18px" },
      { label: "20", value: "20px" },
      { label: "24", value: "24px" },
      { label: "28", value: "28px" },
      { label: "32", value: "32px" }
    ]

    const fontFamilyOptions = [
      { label: "Arial", value: "Arial, Helvetica, sans-serif" },
      { label: "Helvetica", value: "Helvetica, Arial, sans-serif" },
      { label: "Verdana", value: "Verdana, Arial, sans-serif" },
      { label: "Tahoma", value: "Tahoma, Arial, sans-serif" },
      { label: "Trebuchet MS", value: '"Trebuchet MS", Arial, sans-serif' },
      { label: "Georgia", value: "Georgia, serif" },
      { label: "Times New Roman", value: '"Times New Roman", serif' },
      { label: "Courier New", value: '"Courier New", monospace' }
    ]
    const applyFontSize = (value) => {
      if (!editor.value) return;

      selectedFontSize.value = value;
      editor.value.chain().focus().setFontSize(value).run();
    };

    const applyFontFamily = (value) => {
      if (!editor.value) return;

      selectedFontFamily.value = value;
      editor.value.chain().focus().setFontFamily(value).run();
    };

    const applyLink = () => {
      if (!linkUrl.value || !editor.value) return;

      editor.value
          .chain()
          .focus()
          .extendMarkRange("link")
          .setLink({ href: linkUrl.value })
          .run();

      linkUrl.value = "";
    };

    const insertVariable = (value) => {
      const v = (props.variables || []).find((item) => item.value === value);
      if (!v || !editor.value) return;

      const variableClass =
          v.type === "link"
              ? "mail-variable mail-variable-link"
              : "mail-variable";

      editor.value
          .chain()
          .focus()
          .insertContent({
            type: "text",
            text: v.text,
            marks: [
              {
                type: "mailVariable",
                attrs: {
                  class: variableClass
                }
              }
            ]
          })
          .run();
    };

    const fileToResizedBase64 = (file, maxWidth = 500, quality = 0.8) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = () => {
          const image = new window.Image();

          image.onload = () => {
            let width = image.width;
            let height = image.height;

            if (width > maxWidth) {
              height = Math.round((height * maxWidth) / width);
              width = maxWidth;
            }

            const canvas = document.createElement("canvas");
            canvas.width = width;
            canvas.height = height;

            const ctx = canvas.getContext("2d");
            if (!ctx) {
              reject(new Error("Canvas context is unavailable"));
              return;
            }

            ctx.drawImage(image, 0, 0, width, height);

            const mimeType = "image/jpeg";
            const base64 = canvas.toDataURL(mimeType, quality);
            resolve(base64);
          };

          image.onerror = () => reject(new Error("Failed to load image"));
          image.src = reader.result;
        };

        reader.onerror = () => reject(new Error("Failed to read file"));
        reader.readAsDataURL(file);
      });
    };

    const onImageChange = async (event) => {
      const file = event.target.files?.[0];
      if (!file || !editor.value) return;

      try {
        const base64 = await fileToResizedBase64(file, 500, 0.8);

        editor.value
            .chain()
            .focus()
            .setImage({
              src: base64,
              alt: file.name
            })
            .run();

        event.target.value = "";
      } catch (error) {
        console.error("Image insert failed:", error);
      }
    };

    const highlightColors = [
      "#FFE58F",
      "#FFA39E",
      "#B7EB8F",
      "#ADC6FF",
      "#e6ccff"
    ];

    const applyHighlight = (color) => {
      if (!editor.value) return;

      editor.value
          .chain()
          .focus()
          .setHighlight({ color })
          .run();
    };

    const confirmReset = () => {
      emit("reset");
    };

    watch(
        () => props.content,
        (value) => {
          if (!editor.value) return;

          const current = editor.value.getHTML();
          if (value !== current) {
            editor.value.commands.setContent(value, false);
          }
        }
    );

    onBeforeUnmount(() => {
      editor.value?.destroy();
    });

    return {
      editor,
      toolbar,
      linkUrl,
      handleAction,
      isActive,
      applyLink,
      insertVariable,
      onImageChange,
      applyHighlight,
      highlightColors,
      confirmReset,
      applyFontSize,
      fontSizeOptions,
      selectedFontSize,
      fontFamilyOptions,
      applyFontFamily,
      selectedFontFamily,
      imageInput
    };
  }
};
</script>

<style scoped lang="scss">
.mail-editor {
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  background: #fff;
  overflow: hidden;
  font-family: "Open Sans", sans-serif;
}

.toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  padding: 10px 12px;
  border-bottom: 1px solid #f0f0f0;
}

.toolbar-btn,
.toolbar-select {
  height: 32px;
  border: 1px solid #d9d9d9;
  background: #fff;
  border-radius: 6px;
  padding: 0 10px;
  font-size: 14px;
  cursor: pointer;
}

.editor-content {
  padding: 14px;
  min-height: 260px;
}

:deep(.mail-editor-area) {
  min-height: 230px;
  outline: none;
  color: #262626;
  font-size: 14px;
  line-height: 1.6;
}

:deep(.mail-editor-area p) {
  margin: 0 0 4px;
}

:deep(.mail-editor-area ul),
:deep(.mail-editor-area ol) {
  padding-left: 20px;
  margin: 0 0 4px;
}

:deep(.mail-editor-area ol li) {
  list-style: decimal;
}

:deep(.mail-editor-area ul li) {
  list-style: disc;
}

:deep(.mail-variable) {
  font-weight: 500;
}

:deep(.mail-variable-link) {
  color: var(--main-green);
}

:deep(.ant-space) {
  @media (max-width: $mobile-breakpoint) {
    flex-wrap: wrap;
  }
}

.output-block {
  border-top: 1px solid #f0f0f0;
  padding: 12px;
  background: #fcfcfc;
}

.output-title {
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 8px;
}

.output {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 12px;
  color: #595959;
}

.highlight-picker {
  display: flex;
  gap: 6px;
}

.highlight-color {
  width: 22px;
  height: 22px;
  border-radius: 4px;
  cursor: pointer;
  border: 1px solid #d9d9d9;
}

.highlight-color:hover {
  transform: scale(1.05);
}

.reset-template {
  margin-top: 8px;
}

:deep(.mail-editor-area table) {
  border-collapse: collapse;
  width: 100%;
  margin: 8px 0;
  table-layout: fixed;
}

:deep(.mail-editor-area th),
:deep(.mail-editor-area td) {
  border: 1px solid var(--border-grey);
  padding: 8px;
  vertical-align: top;
  min-width: 80px;
}

:deep(.mail-editor-area th) {
  background: #fafafa;
  font-weight: 600;
}

:deep(.mail-editor-area .selectedCell:after) {
  background: var(--polar-green);
  content: "";
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  pointer-events: none;
  position: absolute;
  z-index: 2;
}

:deep(.mail-editor-area td),
:deep(.mail-editor-area th) {
  position: relative;
}

:deep(.reset-template-popconfirm .ant-popover-message) {
  margin-bottom: 8px;
}

.pc-head {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.pc-warn-ic {
  color: var(--sunset-orange);
  font-size: 16px;
  margin-top: 2px;
}

.pc-title {
  font-weight: 600;
  line-height: 20px;
}

.pc-desc {
  max-width: 260px;
  color: #595959;
  line-height: 20px;
}
</style>
